import { 
  User, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  Linkedin, 
  Briefcase, 
  Star,
  Tag,
  Check,
  X,
  Building2,
  GraduationCap,
  Languages,
  Zap,
  FileText
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { getFormattedDateTime } from "@/utils/settings";
import { useSettings } from "@/providers/SettingsProvider";
import { useTranslation } from "react-i18next";
import { Candidate } from "@/types/candidate";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useCandidateStatus } from "@/utils/candidate/statusUtils";
import { formatLinkedInUrl, getLinkedInDisplayText } from "@/utils/candidate/linkedinUtils";
import { CLOSED_MATCH_STATUSES_FILTER } from "@/utils/matches/status";
import { CandidateHiredWorkforceModal } from "./CandidateHiredWorkforceModal";
import { CandidateUnavailableWorkforceModal } from "./CandidateUnavailableWorkforceModal";
import { useCandidateLanguages } from "@/hooks/languages/useCandidateLanguages";
import { CEFR_LEVELS, COMMON_LANGUAGES } from "@/types/candidate";

// LocalStorage cache key and version
const STORAGE_KEY = 'candidate_linkedin_pictures';
const CACHE_VERSION = 2; // Increment when cache structure changes

// Cache utilities for LinkedIn profile pictures
const linkedInPictureCache = {
	get: (candidateId: string) => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (!stored) return null;
			const cache = JSON.parse(stored);
			
			// Check cache version - invalidate if outdated
			if (cache._version !== CACHE_VERSION) {
				localStorage.removeItem(STORAGE_KEY);
				return null;
			}
			
			const entry = cache[candidateId];
			if (!entry) return null;
			
			// Validate entry has required fields (participantId is required in v2)
			if (!entry.participantId) return null;
			
			// Cache valid for 24 hours
			const maxAge = 24 * 60 * 60 * 1000;
			if (Date.now() - entry.updatedAt > maxAge) return null;
			
			return {
				pictureUrl: entry.pictureUrl,
				participantId: entry.participantId
			};
		} catch {
			return null;
		}
	},

	set: (candidateId: string, pictureUrl: string | null, participantId: string | null = null) => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			let cache = stored ? JSON.parse(stored) : {};
			
			// Check cache version - reset if outdated
			if (cache._version !== CACHE_VERSION) {
				cache = { _version: CACHE_VERSION };
			}
			
			cache[candidateId] = {
				pictureUrl,
				participantId,
				updatedAt: Date.now()
			};
			cache._version = CACHE_VERSION;
			
			localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
		} catch {
			// Silent error handling
		}
	}
};

const SKILLS_LIMIT = 12;

const SkillsSection = ({ skills, languages, t }: { skills: string[]; languages: { id: string; language: string; language_code: string; cefr_level: number }[]; t: (key: string, options?: any) => string }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = skills.length > SKILLS_LIMIT;
  const visibleSkills = expanded ? skills : skills.slice(0, SKILLS_LIMIT);

  return (
    <div className="bg-yellow-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold flex items-center mb-3">
        <Zap className="h-4 w-4 mr-2 text-yellow-600" />
        {t("candidates.overview.skills", "Skills")}
      </h3>
      {languages.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {languages.map((lang) => {
              const languageName = COMMON_LANGUAGES[lang.language_code] || lang.language;
              const levelLabel = CEFR_LEVELS[lang.cefr_level as keyof typeof CEFR_LEVELS];
              return (
                <Badge key={lang.id} variant="secondary" className="text-xs">
                  {languageName} ({levelLabel})
                </Badge>
              );
            })}
          </div>
        </div>
      )}
      {skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {visibleSkills.map((skill, index) => (
            <Badge key={index} variant="outline" className="text-xs rounded-sm px-2 py-0.5 font-normal">
              {skill}
            </Badge>
          ))}
          {hasMore && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-blue-500 dark:text-blue-400 hover:underline px-1 py-0.5"
            >
              {expanded
                ? t("common.showLess")
                : t("common.seeAll", { count: skills.length })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface CandidateOverviewCardProps {
  candidate: Candidate;
  formattedDate: string;
  customFieldsConfig?: any[]; // Custom fields configuration for the organization
  cv?: string | null;
  cvExists?: boolean;
  onViewCV?: () => void;
  onCandidateUpdate?: (updatedData: Partial<Candidate>) => Promise<void> | void;
  onStatusChanged?: () => void;
}

export const CandidateOverviewCard = ({
  candidate,
  formattedDate,
  customFieldsConfig = [],
  cv,
  cvExists = false,
  onViewCV,
  onCandidateUpdate,
  onStatusChanged
}: CandidateOverviewCardProps) => {
  const { dateFormat, timeFormat } = useSettings();
  const { t } = useTranslation();
  const { getLocalizedStatus, getStatusDotColor, getStatusOptions } = useCandidateStatus();
  const [managerName, setManagerName] = useState<string>("");
  const [ongoingCount, setOngoingCount] = useState(0);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [loadingPicture, setLoadingPicture] = useState(false);
  const [failedProfilePictures, setFailedProfilePictures] = useState(new Set<string>());
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [statusValue, setStatusValue] = useState<string>(candidate.status?.toLowerCase() || '');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showHiredWorkforceModal, setShowHiredWorkforceModal] = useState(false);
  const [showUnavailableWorkforceModal, setShowUnavailableWorkforceModal] = useState(false);

  useEffect(() => {
    setStatusValue(candidate.status?.toLowerCase() || '');
  }, [candidate.status]);

  // Fetch manager name from profiles table
  useEffect(() => {
    const fetchManagerName = async () => {
      if (candidate.manager) {
        // Use the new database function that provides full access to user names
        const { data, error } = await supabase
          .rpc('get_user_display_info', {
            p_user_id: candidate.manager
          });
        
        if (!error && data && data.length > 0) {
          const userInfo = data[0];
          setManagerName(`${userInfo.first_name || ""} ${userInfo.last_name || ""}`.trim());
        } else {
          setManagerName("");
        }
      } else {
        setManagerName("");
      }
    };
    fetchManagerName();
  }, [candidate.manager]);

  // Fetch ongoing processes count
  useEffect(() => {
    async function fetchOngoingMatches() {
      if (!candidate.id) return;
      const { count, error } = await supabase
        .from('matches')
        .select('id', { count: 'exact', head: false })
        .eq('candidate_id', candidate.id)
        .not('status', 'in', CLOSED_MATCH_STATUSES_FILTER);
      if (!error) setOngoingCount(count || 0);
    }
    fetchOngoingMatches();
  }, [candidate.id]);

  // Fetch LinkedIn profile picture if candidate has LinkedIn profile
  useEffect(() => {
    if (!candidate.linkedin || !candidate.linkedin_json || !candidate.id) return;

    const fetchProfilePicture = async () => {
      // Check cache first
      const cached = linkedInPictureCache.get(candidate.id);
      if (cached !== null) {
        setProfilePictureUrl(cached.pictureUrl);
        if (cached.participantId) {
          setParticipantId(cached.participantId);
        }
        return;
      }

      setLoadingPicture(true);
      try {
        const { data, error } = await supabase.functions.invoke(
          `unipile-handler?command=get-participant-detail-for-entity&entity_type=candidate&entity_id=${candidate.id}`,
          { method: 'GET' }
        );

        if (error) {
          linkedInPictureCache.set(candidate.id, null, null);
          return;
        }

        if (data && !data.error && data.picture_url) {
          setProfilePictureUrl(data.picture_url);
          linkedInPictureCache.set(candidate.id, data.picture_url, data.participant_id);
          if (data.participant_id) {
            setParticipantId(data.participant_id);
          }
        } else {
          linkedInPictureCache.set(candidate.id, null, null);
        }
      } catch (err) {
        linkedInPictureCache.set(candidate.id, null, null);
      } finally {
        setLoadingPicture(false);
      }
    };

    fetchProfilePicture();
  }, [candidate.id, candidate.linkedin]);

  // Handler for profile picture loading errors
  const handleProfilePictureError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (!candidate.id || !participantId) return;

    const pictureKey = candidate.id;
    if (failedProfilePictures.has(pictureKey)) return;

    // Mark as failed to prevent infinite retry loops
    setFailedProfilePictures(prev => new Set(prev).add(pictureKey));

    // Call refresh-participant-picture to get a fresh URL from LinkedIn
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          `unipile-handler?command=refresh-participant-picture&participant_id=${participantId}`,
          { method: 'GET' }
        );

        if (error || (data && data.error)) return;

        if (data && data.picture_url && data.picture_url !== profilePictureUrl) {
          setProfilePictureUrl(data.picture_url);
          linkedInPictureCache.set(candidate.id, data.picture_url, participantId);
          
          // Remove from failed set since we successfully refreshed
          setFailedProfilePictures(prev => {
            const newSet = new Set(prev);
            newSet.delete(pictureKey);
            return newSet;
          });
        }
      } catch (err) {
        // Silent error handling
      }
    })();
  };

  // Helper function to check if a section has data to display
  const hasData = (values: (string | string[] | undefined)[]) => {
    return values.some(value => {
      if (Array.isArray(value)) return value && value.length > 0;
      return !!value;
    });
  };

  // Helper to check if custom fields have values (only for currently configured fields)
  const hasCustomFields = (fields?: Record<string, any>) => {
    if (!fields || !customFieldsConfig.length) return false;
    
    // Only check fields that are currently configured in the admin panel
    return customFieldsConfig.some(fieldConfig => {
      const fieldValue = fields[fieldConfig.name];
      if (fieldConfig.type === 'boolean') {
        // For boolean fields, always show if they have a value (true or false)
        return fieldValue !== undefined;
      }
      // For other fields, check if they have a non-empty value
      if (Array.isArray(fieldValue)) return fieldValue && fieldValue.length > 0;
      return !!fieldValue;
    });
  };

  // Format dates according to user settings
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return getFormattedDateTime(new Date(dateString), dateFormat, timeFormat, false);
  };

  // Helper to format custom field values
  const formatCustomFieldValue = (fieldName: string, fieldValue: any) => {
    // Check if this is a boolean field by looking at the custom fields configuration
    const fieldConfig = customFieldsConfig.find(field => field.name === fieldName);
    const isBooleanField = fieldConfig?.type === 'boolean';
    
    if (isBooleanField) {
      return fieldValue ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-600" />
      );
    }
    
    // For non-boolean fields, return the value as before
    return Array.isArray(fieldValue) ? fieldValue.join(', ') : String(fieldValue);
  };

  // Helper to generate initials from name
  const getInitials = (name: string) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Check which sections have data
  const hasContactInfo = hasData([candidate.email, candidate.phone, candidate.linkedin]);
  const hasLocation = hasData([candidate.location]);
  const hasProfessional = hasData([candidate.manager]);
  const hasSkills = hasData([candidate.skills]);
  const hasCustomFieldData = hasCustomFields(candidate.custom_fields);

  // Fetch languages from DB
  const { languages: candidateLanguages } = useCandidateLanguages(candidate.id);
  
  // Check if custom fields are configured for the organization
  const hasCustomFieldsConfigured = customFieldsConfig.length > 0;
  
  // Determine grid layout based on custom fields presence
  const gridCols = hasCustomFieldsConfigured && hasCustomFieldData ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  const handleStatusChange = async (nextStatus: string) => {
    if (!candidate.id || nextStatus === statusValue)
      return;

    // If changing to unavailable, show combined modal (date + workforce)
    if (nextStatus === 'unavailable') {
      setShowUnavailableWorkforceModal(true);
      return;
    }

    // For other status changes, proceed normally
    await performStatusUpdate(nextStatus, null);
  };

  const performStatusUpdate = async (nextStatus: string, unavailableUntil: string | null) => {
    const previousStatus = statusValue;
    setStatusValue(nextStatus);
    setIsUpdatingStatus(true);

    try {
      const updateData: Partial<Candidate> = { status: nextStatus };
      
      // Add unavailable_until to update data
      if (nextStatus === 'unavailable')
        updateData.unavailable_until = unavailableUntil;
      else
        // Clear unavailable_until when status changes from unavailable to something else
        updateData.unavailable_until = null;

      if (onCandidateUpdate)
        await onCandidateUpdate(updateData);
      else {
        const { error } = await supabase
          .from('candidates')
          .update(updateData)
          .eq('id', candidate.id);

        if (error)
          throw error;
      }

      // When candidate becomes hired or unavailable, close all open matches
      if (nextStatus === 'hired' || nextStatus === 'unavailable') {
        await supabase.rpc('set_match_candidate_not_available', { p_candidate_id: candidate.id });
        onStatusChanged?.();
      }

      toast({
        title: t('common.success', 'Success'),
        description: t('candidates.profile.statusUpdated', 'Candidate status updated')
      });

      // Show workforce modal after successful hire
      if (nextStatus === 'hired')
        setShowHiredWorkforceModal(true);
    } catch {
      setStatusValue(previousStatus);
      toast({
        title: t('common.error', 'Error'),
        description: t('candidates.profile.statusUpdateError', 'Failed to update candidate status'),
        variant: 'destructive'
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Avatar className="h-12 w-12 flex-shrink-0 !rounded-sm">
          {profilePictureUrl && !failedProfilePictures.has(candidate.id) ? (
            <img
              key={profilePictureUrl}
              src={profilePictureUrl}
              alt={candidate.name}
              className="h-full w-full rounded-sm object-cover"
              onError={handleProfilePictureError}
            />
          ) : (
            <AvatarFallback className="bg-primary/10 text-primary text-base font-medium rounded-sm">
              {getInitials(candidate.name)}
            </AvatarFallback>
          )}
        </Avatar>

        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-foreground leading-tight">
            {candidate.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {candidate.function && (
              <span className="text-muted-foreground">{candidate.function}</span>
            )}
            {(candidate.status || statusValue) && (
              <Select value={statusValue} onValueChange={handleStatusChange} disabled={isUpdatingStatus}>
                <SelectTrigger
                  className="h-auto min-w-0 gap-1.5 border-0 bg-transparent px-0 py-0 text-sm shadow-none ring-0 focus:ring-0 focus:ring-offset-0"
                >
                  <div className="flex items-center gap-1.5">
                    <div className={`h-2 w-2 rounded-full ${getStatusDotColor(statusValue || candidate.status)}`} />
                    <span className="text-muted-foreground">
                      {getLocalizedStatus(statusValue || candidate.status)}
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {getStatusOptions(statusValue || candidate.status).map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {ongoingCount > 0 && (
              <span className="text-sm font-medium text-blue-500 dark:text-blue-400">
                {t("candidates.profile.ongoingProcess", { count: ongoingCount })}
              </span>
            )}
          </div>
        </div>

        {cvExists && onViewCV && (
          <button
            onClick={onViewCV}
            className="flex items-center gap-2 px-3 h-9 text-sm font-medium rounded-sm border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
          >
            <FileText className="h-4 w-4" />
            {t("candidates.cv.viewCV", "View CV")}
          </button>
        )}
      </div>

      {/* Info grid — colored section cards like jobs */}
      <div className={`grid ${gridCols} gap-4`}>
        {/* Contact */}
        {hasContactInfo && (
          <div className="bg-blue-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold flex items-center mb-3">
              <Mail className="h-4 w-4 mr-2 text-blue-600" />
              {t("candidates.overview.contact", "Contact")}
            </h3>
            <div className="space-y-2.5">
              {candidate.email && (
                <div>
                  <span className="text-xs text-muted-foreground">{t("candidates.overview.email", "Email")}</span>
                  <a href={`mailto:${candidate.email}`} className="block text-sm text-blue-500 dark:text-blue-400 hover:underline truncate">{candidate.email}</a>
                </div>
              )}
              {candidate.phone && (
                <div>
                  <span className="text-xs text-muted-foreground">{t("candidates.overview.phone", "Phone")}</span>
                  <a href={`tel:${candidate.phone}`} className="block text-sm text-blue-500 dark:text-blue-400 hover:underline">{candidate.phone}</a>
                </div>
              )}
              {candidate.linkedin && (() => {
                const formattedLinkedInUrl = formatLinkedInUrl(candidate.linkedin);
                return (
                  <div>
                    <span className="text-xs text-muted-foreground">{t("candidates.overview.linkedin", "LinkedIn")}</span>
                    {formattedLinkedInUrl ? (
                      <a href={formattedLinkedInUrl} target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-500 dark:text-blue-400 hover:underline truncate">{getLinkedInDisplayText(candidate.linkedin)}</a>
                    ) : (
                      <span className="block text-sm text-muted-foreground">{candidate.linkedin}</span>
                    )}
                  </div>
                );
              })()}
              {candidate.location && (
                <div>
                  <span className="text-xs text-muted-foreground">{t("candidates.overview.city", "City")}</span>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(candidate.location)}`} target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-500 dark:text-blue-400 hover:underline">{candidate.location}</a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* General */}
        <div className="bg-purple-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold flex items-center mb-3">
            <Calendar className="h-4 w-4 mr-2 text-purple-600" />
            {t("candidates.overview.general", "General")}
          </h3>
          <div className="space-y-2.5">
            <div>
              <span className="text-xs text-muted-foreground">{t("candidates.overview.created", "Created")}</span>
              <span className="block text-sm text-foreground">{formattedDate}</span>
            </div>
            {candidate.manager && (
              <div>
                <span className="text-xs text-muted-foreground">{t("candidates.overview.manager", "Manager")}</span>
                <span className="block text-sm text-foreground">{managerName || `User ${candidate.manager.substring(0, 8)}`}</span>
              </div>
            )}
            {statusValue === 'unavailable' && (
              <div>
                <span className="text-xs text-muted-foreground">{t("candidates.overview.unavailableUntil", "Available From")}</span>
                <span className="block text-sm text-foreground">
                  {candidate.unavailable_until
                    ? new Date(candidate.unavailable_until).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                    : t("candidates.profile.notProvided", "Not provided")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Custom Fields */}
        {hasCustomFieldsConfigured && hasCustomFieldData && (
          <div className="bg-red-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold flex items-center mb-3">
              <Tag className="h-4 w-4 mr-2 text-red-600" />
              {t("candidates.customFields.title", "Custom Fields")}
            </h3>
            <div className="space-y-2.5">
              {customFieldsConfig.map((fieldConfig) => {
                const fieldValue = candidate.custom_fields?.[fieldConfig.name];
                if (fieldConfig.type !== 'boolean' && !fieldValue) return null;
                return (
                  <div key={fieldConfig.name}>
                    <span className="text-xs text-muted-foreground">{fieldConfig.name}</span>
                    <div className="text-sm text-foreground">{formatCustomFieldValue(fieldConfig.name, fieldValue)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Skills */}
        {(hasSkills || candidateLanguages.length > 0) && (
          <SkillsSection skills={candidate.skills || []} languages={candidateLanguages} t={t} />
        )}
      </div>
      
      <CandidateHiredWorkforceModal
        isOpen={showHiredWorkforceModal}
        onClose={() => setShowHiredWorkforceModal(false)}
        candidateId={candidate.id}
        candidateName={candidate.name}
        candidateLocation={candidate.location}
        onWorkforceCreated={onStatusChanged}
      />

      <CandidateUnavailableWorkforceModal
        isOpen={showUnavailableWorkforceModal}
        onClose={() => setShowUnavailableWorkforceModal(false)}
        candidateId={candidate.id}
        candidateName={candidate.name}
        currentUnavailableDate={candidate.unavailable_until}
        onConfirm={(date) => performStatusUpdate('unavailable', date)}
        onWorkforceClosed={onStatusChanged}
      />

    </div>
  );
}; 